/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {extendTheme} from '@salesforce/retail-react-app/app/components/shared/ui'
import {overrides as baseOverrides} from './base'
import dynamiteOverrides from './brands/dynamite'

// Brand-specific theme override mappings
const brandOverrides = {
    dynamite: dynamiteOverrides
}

/**
 * French typography rule: wherever English uses Title Case (`textTransform: 'capitalize'`),
 * French uses Sentence case. There is no CSS "sentence case" transform, so we drop the
 * capitalize transform (`'none'`) and let the already sentence-cased French source render
 * as authored. `uppercase` and `none` are left untouched. Applied recursively to the plain
 * override objects (before extendTheme), so it covers every `textStyles` entry and any
 * component style object; functions and primitives pass through unchanged.
 *
 * @param {*} node - a theme override value (object, array, or primitive)
 * @returns {*} a transformed copy for French locales
 */
const toSentenceCaseOverrides = (node) => {
    if (Array.isArray(node)) return node.map(toSentenceCaseOverrides)
    if (node && typeof node === 'object') {
        const out = {}
        for (const [key, value] of Object.entries(node)) {
            out[key] =
                key === 'textTransform' && value === 'capitalize'
                    ? 'none'
                    : toSentenceCaseOverrides(value)
        }
        return out
    }
    return node
}

const isFrenchLocale = (localeId) =>
    typeof localeId === 'string' && localeId.toLowerCase().startsWith('fr')

// Pre-build the base theme and all brand themes at module load time, for both the default
// (English) casing and the French sentence-case variant. extendTheme performs deep merging —
// doing it once avoids runtime cost on navigation.
const baseTheme = extendTheme(baseOverrides)
const themes = {base: baseTheme}
const themesFr = {base: extendTheme(toSentenceCaseOverrides(baseOverrides))}
Object.entries(brandOverrides).forEach(([brandId, overrides]) => {
    themes[brandId] = extendTheme(baseOverrides, overrides)
    themesFr[brandId] = extendTheme(
        toSentenceCaseOverrides(baseOverrides),
        toSentenceCaseOverrides(overrides)
    )
})

/**
 * Returns the pre-built Chakra theme for the given brand and locale.
 * French locales get the sentence-case typography variant. Falls back to the
 * base theme if the brand is not found.
 *
 * @param {string} brandId - e.g. 'dynamite'
 * @param {string} [localeId] - e.g. 'fr-CA' (French locales use sentence case)
 * @returns {object} Chakra UI theme object
 */
export const getThemeForBrand = (brandId, localeId) => {
    const themeMap = isFrenchLocale(localeId) ? themesFr : themes
    return themeMap[brandId] || themeMap.base
}

// Re-export the overrides for backward compatibility
export {overrides} from './base'

// Default export: base theme (used when no brand override applies)
export default baseTheme
